<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from "vue";
import { breakpointsBootstrapV5, useBreakpoints, useEventListener } from "@vueuse/core";
import { TuBasePopup, TuButton, ButtonSeverity } from "@tinyurldev/tu-ui-vue";
import { FocusTrap } from "focus-trap-vue";

import { useTranslation } from "@Resources/js/composables";
import XOverlay from "@Modules/shared/store/XOverlay";
import { logError } from "@Resources/js/helpers/logger";
import type { ApiToken } from "@Modules/teams/Data/Models/ApiToken";

//
// Setup
const overlay = XOverlay();
const { __ } = useTranslation();

//
// Props
interface Props {
  token: ApiToken;
  deleteToken: () => Promise<boolean>;
}

const props = defineProps<Props>();

//
// Data
const isBusy = ref(false);

//
// Computed
const breakpoints = useBreakpoints(breakpointsBootstrapV5);
const isMobileView = breakpoints.smallerOrEqual("xl");

//
// Methods
function close(): void {
  if (isBusy.value) {
    return;
  }

  overlay.hide();
}

async function confirm(): Promise<void> {
  if (isBusy.value) {
    return;
  }

  isBusy.value = true;

  try {
    if (await props.deleteToken()) {
      overlay.hide();
    }
  } catch {
    logError(`[DeleteApiTokenModal]: Token ${props.token.id} could not be deleted.`);
  } finally {
    isBusy.value = false;
  }
}

useEventListener(window, "keydown", (event: KeyboardEvent) => {
  if (event.key === "Escape") {
    close();
  }
});

onMounted(() => {
  document.body.style.overflow = "hidden";
});

onBeforeUnmount(() => {
  document.body.style.overflow = "";
});

//
// Exposed Test Vars
defineExpose({ isBusy, close, confirm });
</script>

<template>
  <div
    class="delete-api-token-modal-wrapper _delete_modal"
    :class="{ 'delete-api-token-modal-wrapper--mobile': isMobileView }"
    @click.self="close"
  >
    <FocusTrap :escape-deactivates="false">
      <TuBasePopup
        id="delete-api-token-modal"
        tabindex="-1"
        class="delete-api-token-modal"
        :class="{ 'delete-api-token-modal--mobile': isMobileView }"
        :is-closeable="!isMobileView"
        @close="close"
      >
        <template #title>
          <span class="_title delete-api-token-modal__title">
            {{ __("organization_api_settings.delete_token_modal.title") }}
          </span>
        </template>

        <template #content>
          <p class="_message delete-api-token-modal__message">
            {{ __("organization_api_settings.delete_token_modal.confirm_message", { name: token.name }) }}
          </p>
        </template>

        <template #footer>
          <div class="delete-api-token-modal__footer">
            <TuButton
              v-if="!isMobileView"
              class="_cancel"
              data-test-id="delete-api-token-cancel"
              :label="__('organization_api_settings.delete_token_modal.cancel_button')"
              is-outlined
              :severity="ButtonSeverity.CANCEL"
              :is-disabled="isBusy"
              @click="close"
            />
            <TuButton
              class="_confirm"
              data-test-id="delete-api-token-submit"
              :label="__('organization_api_settings.delete_token_modal.submit_button')"
              :severity="ButtonSeverity.ALERT"
              :is-block="isMobileView"
              :is-loading="isBusy"
              :is-disabled="isBusy"
              @click="confirm"
            />
          </div>
        </template>
      </TuBasePopup>
    </FocusTrap>
  </div>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;
@use "@tinyurldev/tu-ui-vue/scss/settings/position.scss" as position;
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;

.delete-api-token-modal-wrapper {
  position: fixed;
  inset: 0;
  z-index: position.$highest-z-index;

  display: flex;
  align-items: center;
  justify-content: center;
  padding: spacing.$xxs;

  background-color: color.$main-overlay;

  &--mobile {
    padding: 0;
  }
}

.delete-api-token-modal {
  max-width: 600px;

  &__title {
    font-size: typography.$h5-font-size;
    font-weight: typography.$font-weight-bold;
    line-height: typography.$h5-line-height;
  }

  &__message {
    margin: 0;

    font-size: typography.$base-font-size;
    font-weight: typography.$font-weight-medium;
    line-height: typography.$base-line-height;
  }

  &__footer {
    display: flex;
    gap: spacing.$xxs;
    align-items: end;
    justify-content: flex-end;
  }

  &--mobile {
    margin-top: auto;
  }
}
</style>
